"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get PRESCRIBER_PROFILE_LIMITS () {
        return PRESCRIBER_PROFILE_LIMITS;
    },
    get PRESCRIBER_SNAPSHOT_FIELDS () {
        return PRESCRIBER_SNAPSHOT_FIELDS;
    },
    get PrescriberSnapshotSchema () {
        return PrescriberSnapshotSchema;
    },
    get prescriberSnapshotShape () {
        return prescriberSnapshotShape;
    },
    get refinePrescriberGroups () {
        return refinePrescriberGroups;
    }
});
const _zod = require("zod");
const _identifiers = require("./identifiers");
const PRESCRIBER_PROFILE_LIMITS = {
    printedName: 120,
    specialty: 80,
    ptrIssuedPlace: 80
};
const PRESCRIBER_SNAPSHOT_FIELDS = [
    'printedName',
    'specialty',
    'prcLicenseNo',
    'prcExpiresOn',
    'ptrNo',
    'ptrIssuedOn',
    'ptrIssuedPlace',
    's2LicenseNo',
    's2ExpiresOn'
];
/** PTR details are recorded together or not at all. */ const PTR_GROUP = [
    'ptrNo',
    'ptrIssuedOn',
    'ptrIssuedPlace'
];
/** So are the S2 licence number and its expiry. */ const S2_GROUP = [
    's2LicenseNo',
    's2ExpiresOn'
];
const PRC_FORMAT_MESSAGE = 'PRC licence number must be 6 to 12 letters and digits, with no spaces or dashes.';
function isCalendarDate(value) {
    return _zod.z.iso.date().safeParse(value).success;
}
function blankToNull(value) {
    if (value === undefined || value === null || value === '') {
        return null;
    }
    return value;
}
/**
 * True for U+0000 to U+001F and U+007F. Postgres can't store NUL in `text`,
 * and a line break or tab has no place in a line printed on a prescription.
 */ function hasControlCharacter(value) {
    for(let index = 0; index < value.length; index += 1){
        const code = value.charCodeAt(index);
        if (code <= 0x1f || code === 0x7f) {
            return true;
        }
    }
    return false;
}
const controlCharacterMessage = (label)=>`${label} must not contain line breaks, tabs or other control characters.`;
/**
 * Required single-line text: trimmed, then 1 to `max` characters, none of
 * them a control character. Messages never quote the value.
 */ const requiredText = (max, label)=>_zod.z.string({
        error: `${label} is required.`
    }).trim().min(1, {
        error: `${label} is required.`
    }).max(max, {
        error: `${label} must be at most ${String(max)} characters.`
    }).refine((value)=>!hasControlCharacter(value), {
        error: controlCharacterMessage(label)
    });
/**
 * Optional single-line text: trimmed; blank, `null` and absent all become
 * `null`, so the stored and returned value is never `""`. No control
 * characters. Messages are fixed strings and never quote the value.
 */ const optionalText = (max, label)=>_zod.z.string({
        error: `${label} must be text.`
    }).trim().max(max, {
        error: `${label} must be at most ${String(max)} characters.`
    }).refine((value)=>!hasControlCharacter(value), {
        error: controlCharacterMessage(label)
    }).nullable().optional().transform(blankToNull);
/** A real `YYYY-MM-DD` calendar date (`2026-02-30` is rejected). No range rule. */ const requiredDate = (label)=>_zod.z.iso.date({
        error: `${label} must be a valid date (YYYY-MM-DD).`
    });
const optionalDate = (label)=>_zod.z.string({
        error: `${label} must be a valid date (YYYY-MM-DD).`
    }).trim().refine((value)=>value === '' || isCalendarDate(value), {
        error: `${label} must be a valid date (YYYY-MM-DD).`
    }).nullable().optional().transform(blankToNull);
/** Optional PTR / S2 number: trimmed and upper-cased, then the loose pattern. */ const optionalReferenceNo = (label)=>_zod.z.string({
        error: `${label} must be text.`
    }).trim().toUpperCase().refine((value)=>value === '' || _identifiers.PRESCRIBER_REFERENCE_NO_PATTERN.test(value), {
        error: `${label} must be 3 to 20 letters, digits, spaces or dashes.`
    }).nullable().optional().transform(blankToNull);
const prescriberSnapshotShape = {
    printedName: requiredText(PRESCRIBER_PROFILE_LIMITS.printedName, 'Printed name'),
    specialty: optionalText(PRESCRIBER_PROFILE_LIMITS.specialty, 'Specialty'),
    prcLicenseNo: _zod.z.string({
        error: 'PRC licence number is required.'
    }).trim().toUpperCase().min(1, {
        error: 'PRC licence number is required.'
    }).regex(_identifiers.PRC_LICENSE_NO_PATTERN, {
        error: PRC_FORMAT_MESSAGE
    }),
    prcExpiresOn: requiredDate('PRC expiry date'),
    ptrNo: optionalReferenceNo('PTR number'),
    ptrIssuedOn: optionalDate('PTR issue date'),
    ptrIssuedPlace: optionalText(PRESCRIBER_PROFILE_LIMITS.ptrIssuedPlace, 'PTR place of issue'),
    s2LicenseNo: optionalReferenceNo('S2 licence number'),
    s2ExpiresOn: optionalDate('S2 expiry date')
};
function isPartial(value, group) {
    const filled = group.filter((field)=>value[field] !== null && value[field] !== undefined);
    return filled.length > 0 && filled.length < group.length;
}
function refinePrescriberGroups(value, ctx) {
    if (isPartial(value, PTR_GROUP)) {
        ctx.addIssue({
            code: 'custom',
            path: [
                'ptrNo'
            ],
            message: 'Enter the PTR number, its issue date and its place of issue, or leave all three blank.'
        });
    }
    if (isPartial(value, S2_GROUP)) {
        ctx.addIssue({
            code: 'custom',
            path: [
                's2LicenseNo'
            ],
            message: 'Enter the S2 licence number and its expiry date, or leave both blank.'
        });
    }
}
const PrescriberSnapshotSchema = _zod.z.object(prescriberSnapshotShape).strict().superRefine(refinePrescriberGroups);
